import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useAtomValue, useSetAtom } from "jotai";
import { useMemo } from "react";
import { atomWithStorage } from "jotai/utils";

import SharedRecordAPI from "../../api/api/SharedRecordAPI";
import { setSchemaAtom } from "../../atoms/GlobalAtoms";
import { View } from "../../types/PageInterfaces";
import { Module } from "../../types/TenantInterfaces";

export const recordPagePasswordAtom = atomWithStorage<Record<string, string>>(
  "record-page-passwords",
  {},
  undefined,
  { getOnInit: true }
);

interface Props {
  success: boolean;
  requiresPassword: boolean;
  parentView: View;
  subViews: View[];
  record: any;
  documentRecords: any[];
  schema: { CRM: { Modules: Module[] } };
}

export default function useSharedRecordPage(
  tenantID: string,
  sharedRecordPageID: string
) {
  const recordPagePasswords = useAtomValue(recordPagePasswordAtom);
  const setSchema = useSetAtom(setSchemaAtom);
  const client = useQueryClient();

  const password = useMemo(() => {
    if (!tenantID || !sharedRecordPageID) return null;

    return recordPagePasswords[`${tenantID}-${sharedRecordPageID}`] || null;
  }, [recordPagePasswords, tenantID, sharedRecordPageID]);

  const sharedRecordPage = useQuery<Props>({
    queryKey: ["shared-record-page", sharedRecordPageID],
    queryFn: () => {
      return SharedRecordAPI.GetSharedRecordPage(
        sharedRecordPageID,
        tenantID,
        password || ""
      ).then((data) => {
        if (!data.data.data.success) return data.data.data;

        for (const record of data.data.data?.documentRecords || []) {
          client.setQueryData(
            [tenantID, "record", "Documents", record._id],
            record
          );
        }

        for (const flow of data.data.data?.flowCharts || []) {
          client.setQueryData([tenantID, "record", "Flows", flow._id], flow);
        }

        setSchema(data.data.data.schema);

        return data.data.data;
      });
    },
    enabled: !!tenantID && !!sharedRecordPageID,
    staleTime: Infinity,
  });

  return {
    sharedRecordPage: sharedRecordPage.data,
    sharedRecordPageLoading: sharedRecordPage.isLoading,
  };
}
